import { useCallback, useEffect, useRef, useState } from 'react';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { useAuth } from './contexts/AuthContext';
import { useRepoContext } from './contexts/RepoContext';
import { AppShell } from './components/AppShell';
import { RepoNav } from './components/RepoNav';
import { DocViewer } from './components/DocViewer';
import { CommentPanel } from './components/CommentPanel';
import { apiConfig } from './authConfig';
import { buildRoute, parseRoute } from './utils/route';

function SignedOutView() {
    const { login } = useAuth();
    return (
        <div className="signin-page">
            <div className="signin-page__card">
                <img src="/logo.png" alt="BACR" className="signin-page__logo" />
                <h1>{apiConfig.tenantDisplayName} <span className="signin-page__product">docreview</span></h1>
                <p>Review documentation from BACR's private repositories.</p>
                <button className="btn btn--primary signin-page__button" onClick={() => void login()}>
                    Sign in
                </button>
                <p className="signin-page__fine-print">
                    Restricted to invited <code>@bacr.org</code> accounts. Contact your administrator if you need access.
                </p>
            </div>
        </div>
    );
}

function AuthedApp() {
    const { tokenReady, userInfo, authError } = useAuth();
    const { effectiveRepoId, selectRepo } = useRepoContext();

    // Initialize state from URL on first render.
    const initialRoute = useRef(parseRoute(window.location.pathname));
    const [selectedPath, setSelectedPath] = useState<string | null>(initialRoute.current.path);
    const [commitSha, setCommitSha] = useState<string | null>(null);
    const [pendingQuote, setPendingQuote] = useState<string | null>(null);
    const [pendingRange, setPendingRange] = useState<{ startLine: number; startCol: number; endLine: number; endCol: number } | null>(null);
    const [pendingAnnotation, setPendingAnnotation] = useState<{ drawingId: string; previewDataUrl: string } | null>(null);
    const [pendingOpen, setPendingOpen] = useState<{ repoId: string; path: string } | null>(null);
    const [asideRequestOpen, setAsideRequestOpen] = useState(0);
    // Panel visibility lives here so reader toolbar buttons can toggle it too.
    const [navOpen, setNavOpen] = useState(true);
    const [asideOpen, setAsideOpen] = useState(true);

    // After the repo list is available, restore the URL's repo selection.
    // Done once per initial load — only when there isn't already a repo set.
    const initialRepoRestored = useRef(false);
    useEffect(() => {
        if (initialRepoRestored.current) return;
        if (!initialRoute.current.repoId) return;
        // Wait for RepoContext to know what's allowed before pushing a select.
        // selectRepo is a no-op for unknown ids; just call once we're ready.
        if (effectiveRepoId !== initialRoute.current.repoId) {
            selectRepo(initialRoute.current.repoId);
        }
        initialRepoRestored.current = true;
    }, [effectiveRepoId, selectRepo]);

    // Cross-repo navigation queue (AI search → click result)
    useEffect(() => {
        if (pendingOpen && effectiveRepoId === pendingOpen.repoId) {
            setSelectedPath(pendingOpen.path);
            setCommitSha(null);
            setPendingQuote(null);
            setPendingOpen(null);
        }
    }, [pendingOpen, effectiveRepoId]);

    // URL <- state sync. Push whenever the URL needs to change.
    useEffect(() => {
        const next = buildRoute({
            view: 'docs',
            repoId: effectiveRepoId ?? null,
            path: selectedPath,
            adminTab: 'org'
        });
        if (window.location.pathname !== next) {
            window.history.pushState({}, '', next);
        }
    }, [effectiveRepoId, selectedPath]);

    // Back/forward → re-read state from URL.
    useEffect(() => {
        function onPop() {
            const r = parseRoute(window.location.pathname);
            if (r.repoId && r.repoId !== effectiveRepoId) selectRepo(r.repoId);
            setSelectedPath(r.path);
            setCommitSha(null);
            setPendingQuote(null);
        }
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, [effectiveRepoId, selectRepo]);

    const onOpenDoc = useCallback(
        (repoId: string, path: string) => {
            if (effectiveRepoId === repoId) {
                setSelectedPath(path);
                setCommitSha(null);
                setPendingQuote(null);
            } else {
                setPendingOpen({ repoId, path });
                selectRepo(repoId);
            }
        },
        [effectiveRepoId, selectRepo]
    );

    const onFileLoaded = useCallback((info: { commitSha: string }) => {
        setCommitSha(info.commitSha);
    }, []);
    const onQuoteSelected = useCallback((quote: string, range: { startLine: number; startCol: number; endLine: number; endCol: number } | null) => {
        setPendingQuote(quote);
        setPendingRange(range);
        setAsideRequestOpen((n) => n + 1);
    }, []);
    const onConsumeQuote = useCallback(() => {
        setPendingQuote(null);
        setPendingRange(null);
    }, []);
    const onAnnotationSaved = useCallback((info: { drawingId: string; previewDataUrl: string }) => {
        setPendingAnnotation(info);
        setAsideRequestOpen((n) => n + 1);
    }, []);
    const onConsumeAnnotation = useCallback(() => {
        setPendingAnnotation(null);
    }, []);
    const onSelectPath = useCallback((path: string) => {
        setSelectedPath(path);
        setCommitSha(null);
        setPendingQuote(null);
    }, []);

    if (!tokenReady) {
        return <div className="full-page-loader">Loading…</div>;
    }
    if (authError) {
        return (
            <div className="signin-page">
                <div className="signin-page__card signin-page__card--error">
                    <img src="/logo.png" alt="BACR" className="signin-page__logo" />
                    <h1>Access denied</h1>
                    <p>{authError}</p>
                </div>
            </div>
        );
    }
    if (!userInfo) {
        return (
            <div className="signin-page">
                <div className="signin-page__card">
                    <img src="/logo.png" alt="BACR" className="signin-page__logo" />
                    <h1>Loading account…</h1>
                </div>
            </div>
        );
    }

    return (
        <AppShell
            onOpenDoc={onOpenDoc}
            asideRequestOpen={asideRequestOpen}
            navOpen={navOpen}
            asideOpen={asideOpen}
            onSetAsideOpen={setAsideOpen}
            nav={<RepoNav selectedPath={selectedPath} onSelectPath={onSelectPath} />}
            main={
                <DocViewer
                    path={selectedPath}
                    onFileLoaded={onFileLoaded}
                    onQuoteSelected={onQuoteSelected}
                    onSelectPath={onSelectPath}
                    onAnnotationSaved={onAnnotationSaved}
                    navOpen={navOpen}
                    onToggleNav={() => setNavOpen((v) => !v)}
                    asideOpen={asideOpen}
                    onToggleAside={() => setAsideOpen((v) => !v)}
                />
            }
            aside={
                selectedPath ? (
                    <CommentPanel
                        repoId={effectiveRepoId}
                        path={selectedPath}
                        commitSha={commitSha}
                        selectedQuote={pendingQuote}
                        selectedRange={pendingRange}
                        onConsumeQuote={onConsumeQuote}
                        pendingAnnotation={pendingAnnotation}
                        onConsumeAnnotation={onConsumeAnnotation}
                    />
                ) : undefined
            }
        />
    );
}

export function App() {
    return (
        <>
            <UnauthenticatedTemplate>
                <SignedOutView />
            </UnauthenticatedTemplate>
            <AuthenticatedTemplate>
                <AuthedApp />
            </AuthenticatedTemplate>
        </>
    );
}
